import React, { useState, useRef, useEffect } from 'react'
import { type Swiper as SwiperCore } from 'swiper'
import { getYoutubeEmbedUrl } from '#app/shared/lib/misc.js'
import { Icon } from '../../icons/Icon.tsx'
import { Dialog } from '../../popups/Dialog.tsx'
import { Carousel } from './Carousel.tsx'
import { type GalleryMediaItem } from './StickyFullHeightGallery.tsx'

type Props = {
  open: boolean
  onClose: () => void
  mediaItems?: GalleryMediaItem[]
  imagesArray?: string[]
  initialIndex: number
}

export const FullScreenGallery = ({
  open,
  onClose,
  mediaItems: mediaItemsProp,
  imagesArray,
  initialIndex,
}: Props) => {
  const mediaItems: GalleryMediaItem[] =
    mediaItemsProp ?? (imagesArray ?? []).map((url) => ({ type: 'image', url }))
  const [swiper, setSwiper] = useState<SwiperCore | undefined>(undefined)
  const [activeIndex, setActiveIndex] = useState(
    mediaItems.length > 0 ? initialIndex : 0,
  )
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isZoomed, setIsZoomed] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const activeItem = mediaItems[activeIndex]
  const isVideoSlide = activeItem?.type === 'video'

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  useEffect(() => {
    setIsZoomed(false)
  }, [activeIndex])

  const handlePrevClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    swiper?.slidePrev()
  }

  const handleNextClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    swiper?.slideNext()
  }

  const handleZoomToggle = (event: React.MouseEvent) => {
    if (isVideoSlide) return
    if (swiper) {
      const zoom = swiper.zoom
      if (isZoomed) {
        zoom.out()
      } else {
        zoom.in(1.5)
      }
      setIsZoomed(!isZoomed)
    }
    event.stopPropagation()
  }

  const isFirstSlide = activeIndex === 0
  const isLastSlide =
    mediaItems.length === 0 ? true : activeIndex === mediaItems.length - 1

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="!rounded-none border-none bg-neutral-900"
      overlayClassName="bg-neutral-900"
      containerClassName="overflow-hidden"
      variant="fullscreen"
      hideIcon
    >
      <div
        ref={containerRef}
        className={`
          relative flex h-screen w-full select-none items-center justify-center
        `}
      >
        <Carousel
          initialIndex={mediaItems.length > 0 ? initialIndex : 0}
          slides={mediaItems.map((item, index) =>
            item.type === 'video' ? (
              <div
                key={index}
                className="flex h-screen w-full items-center justify-center p-4"
                role="button"
                tabIndex={0}
                onClick={onClose}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onClose()
                }}
              >
                <iframe
                  title="Vídeo del coche"
                  src={
                    getYoutubeEmbedUrl(item.url, {
                      muted: true,
                      autoplay: false,
                    }) ?? ''
                  }
                  className={`
                    aspect-video max-h-[784px] w-full max-w-[1176px] rounded-md
                  `}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div
                key={index}
                className="flex h-screen w-full items-center justify-center"
                role="button"
                tabIndex={0}
                onClick={onClose}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onClose()
                }}
              >
                {/* eslint-disable-next-line */}
                <img
                  src={item.url}
                  alt={`Imagen ${index + 1} de ${mediaItems.length}`}
                  className={`
                    max-h-[784px] w-full max-w-[1176px] rounded-md
                    object-contain
                  `}
                  onClick={handleZoomToggle}
                />
              </div>
            ),
          )}
          slidesPerView={1}
          swiper={swiper}
          setSwiper={setSwiper}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          zoom={!isVideoSlide}
          onZoomChange={(zoomed) => setIsZoomed(zoomed)}
          className="w-full"
          swiperOptions={{
            centeredSlides: true,
            spaceBetween: 0,
            watchOverflow: true,
            resistance: true,
            resistanceRatio: 0.85,
          }}
        />

        <div className="absolute right-4 top-4 z-[999] flex items-center gap-2">
          {!isVideoSlide && (
            <button
              type="button"
              onClick={handleZoomToggle}
              className={`
                rounded-full p-2 text-white

                hover:bg-white/10
              `}
            >
              <Icon
                name={isZoomed ? 'basic/zoom-out' : 'basic/zoom-in'}
                size="24px"
                className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]"
              />
            </button>
          )}

          <button
            type="button"
            onClick={async () => {
              try {
                if (isFullscreen) {
                  await document?.exitFullscreen()
                } else if (containerRef.current) {
                  await containerRef.current.requestFullscreen()
                }
              } catch (error) {
                console.error('Error toggling fullscreen:', error)
              }
            }}
            className={`
              rounded-full p-2 text-white

              hover:bg-white/10
            `}
          >
            <Icon
              name={isFullscreen ? 'basic/minimize' : 'basic/maximize'}
              size="24px"
              className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]"
            />
          </button>

          <button
            type="button"
            onClick={onClose}
            className={`
              rounded-full p-2 text-white

              hover:bg-white/10
            `}
          >
            <Icon
              name="basic/x"
              size="24px"
              className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]"
            />
          </button>
        </div>

        {!isFirstSlide && (
          <button
            type="button"
            className={`
              absolute left-4 top-1/2 z-[999] hidden -translate-y-1/2
              rounded-full bg-white/30 p-2 text-white

              hover:bg-white/50

              lg:block
            `}
            onClick={handlePrevClick}
          >
            <Icon name="basic/chevron-left" size="24px" />
          </button>
        )}
        {!isLastSlide && (
          <button
            type="button"
            className={`
              absolute right-4 top-1/2 z-[999] hidden -translate-y-1/2
              rounded-full bg-white/30 p-2 text-white

              hover:bg-white/50

              lg:block
            `}
            onClick={handleNextClick}
          >
            <Icon name="basic/chevron-right" size="24px" />
          </button>
        )}

        <div
          className={`
            absolute bottom-4 left-0 flex w-full items-center justify-between
            px-4

            lg:hidden
          `}
        >
          <button
            type="button"
            className={`
              rounded-full bg-white/30 p-2 text-white

              disabled:opacity-50

              hover:bg-white/50
            `}
            onClick={handlePrevClick}
            disabled={isFirstSlide}
          >
            <Icon name="basic/chevron-left" size="24px" />
          </button>
          <p className="text-subtitle1-bold text-white">
            <span>{activeIndex + 1}</span>/<span>{mediaItems.length}</span>
          </p>
          <button
            type="button"
            className={`
              rounded-full bg-white/30 p-2 text-white

              disabled:opacity-50

              hover:bg-white/50
            `}
            disabled={isLastSlide}
            onClick={handleNextClick}
          >
            <Icon name="basic/chevron-right" size="24px" />
          </button>
        </div>
      </div>
    </Dialog>
  )
}
